import apiClient from './client';
import { Company, CompanyStats } from '../types/user';

export interface CreateCompanyData {
  name: string;
  slug: string;
  subscription: {
    plan: 'trial' | 'basic' | 'premium' | 'enterprise';
    vehicleLimit: number;
  };
}

export interface UpdateCompanyData {
  name?: string;
  subscription?: {
    plan?: 'trial' | 'basic' | 'premium' | 'enterprise';
    vehicleLimit?: number;
  };
}

export interface CompanyListResponse {
  success: boolean;
  data: Company[];
}

export interface CompanyDetailResponse {
  success: boolean;
  data: {
    company: Company;
    stats: CompanyStats;
  };
}

export const companyApi = {
  // List all companies (SuperAdmin only)
  listCompanies: async (): Promise<CompanyListResponse> => {
    const response = await apiClient.get<CompanyListResponse>('/api/companies');
    return response.data;
  },

  // Get company details and stats (SuperAdmin only)
  getCompanyDetails: async (companyId: string): Promise<CompanyDetailResponse> => {
    const response = await apiClient.get<CompanyDetailResponse>(`/api/companies/${companyId}`);
    return response.data;
  },

  // Get current user's own company (any authenticated user with a company)
  getMyCompany: async (): Promise<{ success: boolean; data: Company }> => {
    const response = await apiClient.get<{ success: boolean; data: Company }>('/api/companies/my');
    return response.data;
  },

  // Create new company (SuperAdmin only)
  createCompany: async (data: CreateCompanyData): Promise<{ success: boolean; data: Company }> => {
    const response = await apiClient.post<{ success: boolean; data: Company }>(
      '/api/companies',
      data
    );
    return response.data;
  },

  // Update company (SuperAdmin only)
  updateCompany: async (
    companyId: string,
    data: UpdateCompanyData
  ): Promise<{ success: boolean; data: Company }> => {
    const response = await apiClient.patch<{ success: boolean; data: Company }>(
      `/api/companies/${companyId}`,
      data
    );
    return response.data;
  },

  // Update company status (SuperAdmin only)
  updateCompanyStatus: async (
    companyId: string,
    status: 'active' | 'suspended' | 'deleted'
  ): Promise<{ success: boolean; data: Company }> => {
    const response = await apiClient.patch<{ success: boolean; data: Company }>(
      `/api/companies/${companyId}/status`,
      { status }
    );
    return response.data;
  },

  // Update GPS visibility for company users (SuperAdmin only)
  updateGpsVisibility: async (
    companyId: string,
    enabled: boolean
  ): Promise<{ success: boolean; data: Company }> => {
    const response = await apiClient.patch<{ success: boolean; data: Company }>(
      `/api/companies/${companyId}/gps-visibility`,
      { enabled }
    );
    return response.data;
  },

  deleteCompany: async (companyId: string): Promise<{
    success: boolean;
    data: {
      deleted_company_id: string;
      deleted_vehicles: number;
      deleted_users: number;
      deleted_trips: number;
      deleted_notifications: number;
      unlinked_obd_devices: number;
    };
  }> => {
    const response = await apiClient.delete(`/api/companies/${companyId}`);
    return response.data;
  },
};
